import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { RelatedChannels } from "@/components/calv/RelatedChannels";
import { CTABlock } from "@/components/calv/CTABlock";
import { Callout } from "@/components/calv/Callout";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";

export const Route = createFileRoute("/leistungen/performance-marketing/tiktok-ads")({
  head: () => {
    const h = pageHead("pmTiktokAds", "de");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(serviceSchema({
          name: "TikTok Ads Agentur",
          description: "Spark Ads, Creator-Sourcing, Top-View — gesteuert auf wirtschaftlichen Beitrag, nicht auf Vanity-Views.",
          url: "/leistungen/performance-marketing/tiktok-ads",
        })),
        jsonLdScript(breadcrumbSchema([
          { name: "Start", path: "/" },
          { name: "Leistungen", path: "/leistungen" },
          { name: "Performance Marketing", path: "/leistungen/performance-marketing" },
          { name: "TikTok Ads", path: "/leistungen/performance-marketing/tiktok-ads" },
        ])),
      ],
    };
  },
  component: TikTokAdsDE,
});

const steps = [
  { num: "01", title: "Analyse & Creator-Marktcheck (3–5 Tage)", body: "Account-Audit (falls bereits TikTok-Aktivität läuft), Marken-Tonalitäts-Check für TikTok-Tauglichkeit, Creator-Recherche im relevanten Themenfeld, Wettbewerbs-Analyse. Output: konkrete Hypothesen plus realistisches Erwartungs-Management. Welche Creator-Profile passen, welche nicht." },
  { num: "02", title: "Setup, Tracking & erste Spark-Ad-Pipeline (1–2 Wochen)", body: "TikTok Pixel plus Events API härten. Account-Struktur an wirtschaftlicher Logik ausrichten. Erste Creator-Verträge und Spark-Ad-Code-Übergaben. Parallel erste picazzoo-Variationen für Asset-Tests, falls Creator-Content noch nicht ausreicht. Ziel: produktive erste Kampagnen am Ende der zweiten Woche." },
  { num: "03", title: "Creator-Iteration & Spark-Ad-Skalierung (ab Woche 2–3)", body: "Wöchentlicher Iterations-Rhythmus auf Creator-Performance, Format-Variation, Audience-Match. Spark Ads als Hauptformat plus In-Feed-Ads als Skalierungs-Layer. Klare Lernzyklen statt Random-Testing." },
  { num: "04", title: "Skalierung & laufender Betrieb (kontinuierlich)", body: "Budget-Skalierung in funktionierenden Creator-Audience-Kombinationen. Aufbau eines stabilen Creator-Pools für kontinuierliche Asset-Versorgung. Monatlicher Review auf wirtschaftlichen Kennzahlen, nicht auf View-Count oder Engagement-Rate." },
];

const capabilities = [
  { name: "Spark Ads als Hauptformat", detail: "Native Authentizität · Performance-Skalierung" },
  { name: "Creator-Sourcing & Vertrags-Management", detail: "Mikro · Nano · stabiler Pool" },
  { name: "In-Feed Ads & Top View", detail: "Skalierungs-Layer über Spark hinaus" },
  { name: "TikTok Pixel & Events API", detail: "Server-side · Match-Quality" },
  { name: "Asset-Produktion mit picazzoo", detail: "Markentreu · TikTok-Format-tauglich" },
  { name: "Account-Operations mit GonzalAds", detail: "Bulk · Asset-Management" },
  { name: "Funnel-Architektur kanal-spezifisch", detail: "Andere Logik als Meta" },
  { name: "Reporting & Steuerung", detail: "Wirtschaftliche KPIs statt Views" },
];

const audience = [
  { tag: "Primär", title: "B2C E-Commerce mit visuellen Produkten", body: "TikTok funktioniert besonders stark für Produkte, die visuell erlebbar sind — Mode, Kosmetik, Pet, Home, Sport, Food. Sinnvoll ab Monatsbudgets von etwa €3.000, voll-skalierbar. Funktioniert für etablierte Marken und für DTC-Newcomer gleichermaßen." },
  { tag: "Sekundär", title: "DTC-Marken im Aufbau", body: "TikTok ist für junge DTC-Marken oft der wirtschaftlichste Skalierungs-Kanal — niedrigere CPCs als Meta, höhere organische Reichweiten-Hebel über Spark Ads. Voraussetzung: starke Marken-Story und visuelle Vermittlungsfähigkeit." },
  { tag: "Tertiär", title: "Erlebnis-Dienstleistungen", body: "TikTok funktioniert für Reise, Events, Gastronomie, Wellness — wo das Erlebnis visuell vermittelt werden kann. Für klassische Dienstleistungen ohne visuellen Hebel ist TikTok selten der richtige Kanal." },
  { tag: "Disqualifikation", title: "Wann wir nicht der richtige Partner sind", body: "Bei Monatsbudgets unter €2.000 — Creator-Netzwerk plus systematisches Spark-Ad-Management lohnt sich nicht. Wenn die Erwartung “virale Hits” ist — wir machen Performance-Marketing, kein Influencer-Management. Bei klassischem B2B oder reinen Service-Geschäften." },
];

const realistic = [
  "CPC-Vorteil von 30–50 % gegenüber Meta ist in den meisten DACH-Setups realistisch — TikTok hat im B2C-Bereich noch deutlich niedrigere Auktions-Drücke als Meta.",
  "Spark-Ad-Performance-Vorteil von 40–80 % gegenüber In-Feed-Ads (gleiche Audience, gleicher Spend) ist eine typische Bandbreite — native Creator-Authentizität schlägt klassische Asset-Optik fast immer.",
  "ROAS-Steigerung von 30–60 % gegenüber unstrukturiertem Influencer-Marketing ist realistisch, wenn vorher mit reinen Reichweiten-Verträgen ohne Spark-Ad-Skalierung gearbeitet wurde.",
  "Skalierungs-Hebel von 3–8x bei gleichem ROAS ist möglich, wenn der Creator-Pool stabil ist und das Tracking-Setup belastbar funktioniert.",
];

const value = [
  ["Performance-Steuerung statt View-Vanity.", "Wir reporten auf wirtschaftlichen Beitrag, nicht auf View-Count oder Engagement-Rate."],
  ["Stabiler Creator-Pool als Asset.", "Aufbau eines wiederkehrenden Creator-Netzwerks statt One-off-Influencer-Deals."],
  ["Spark Ads als systematischer Hebel.", "Native Authentizität mit wiederholbarer Performance-Skalierung."],
  ["picazzoo-Asset-Backup für Skalierung.", "Wenn Creator-Content nicht reicht: KI-getriebene Asset-Produktion in Markentonalität."],
  ["Operative Steuerung durch erfahrene Hand.", "In den meisten Mandaten direkt durch den Geschäftsführer."],
  ["Vergütungs-Modell ohne Budget-Kopplung.", "Wir verdienen nicht mehr, wenn Sie mehr Budget einsetzen."],
];

const related = [
  { to: "/leistungen/performance-marketing/meta-ads", name: "Meta Ads", sub: "Komplementärer B2C-Kanal mit eigener Funnel-Logik" },
  { to: "/leistungen/picazzoo", name: "picazzoo", sub: "KI-Pipeline für skalierbare Asset-Produktion" },
  { to: "/leistungen/performance-marketing/youtube-demand-gen", name: "YouTube & Demand Gen", sub: "Erweiterte Video-Performance-Strategien jenseits TikTok" },
];

const channelLinks = [
  { to: "/leistungen/performance-marketing/meta-ads", name: "Werbung auf Facebook & Instagram", sub: "Performance auf Instagram und Facebook mit Creative-Disziplin." },
  { to: "/leistungen/performance-marketing/youtube-demand-gen", name: "YouTube Demand Gen", sub: "Video-Performance auf YouTube für Awareness und Mid-Funnel-Conversion." },
];

function TikTokAdsDE() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Performance Marketing · TikTok Ads"
          headline={<>TikTok Ads Agentur — <em>Visual-Performance ohne Trend-Hetze,</em> mit Creator-Disziplin.</>}
          lead="Spark Ads, Creator-Sourcing, Top-View-Strategien — gesteuert auf wirtschaftlichen Beitrag, nicht auf Vanity-Views. Calvarius arbeitet als TikTok Ads Agentur für E-Commerce-Marken und DTC-Setups, die TikTok als Performance-Kanal verstehen — nicht als Influencer-Spielplatz ohne Steuerung."
          metaStrip={["B2C-Fokus", "Creator-Netzwerk", "Spark-Ads-Disziplin"]}
          breadcrumbs={[
            { label: "Start", to: "/" },
            { label: "Leistungen", to: "/leistungen" },
            { label: "Performance Marketing", to: "/leistungen/performance-marketing" },
            { label: "TikTok Ads" },
          ]}
        />

        <Section number="02" kicker="Standpunkt" headline={<>Was eine TikTok Ads Agentur jenseits der <em>Influencer-Landschaft</em> leistet.</>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Wer eine TikTok Ads Agentur jenseits der Influencer-Landschaft sucht, hat in der Regel eine klare Beobachtung gemacht: TikTok hat sich zu einem ernstzunehmenden Performance-Kanal entwickelt, aber der Markt der TikTok-Dienstleister ist überwiegend Influencer-Marketing-getrieben — viel Reichweite, viele Views, wenig wirtschaftliche Steuerung. Wer TikTok Ads als Brand-Awareness-Spielwiese betreibt, verbrennt Budget. Wer sich auf virale Hoffnungen verlässt, hat keine Strategie. Wer aber Creator-Sourcing, Spark Ads und systematische Performance-Steuerung kombiniert, kommt in die Zone, in der TikTok für viele E-Commerce-Setups zum effizientesten Social-Media-Kanal wird.</p>
              <p className="mb-5">Calvarius behandelt TikTok wie jeden anderen Performance-Kanal — mit klarer wirtschaftlicher Steuerung, nicht mit Trend-Hetze. Das bedeutet: Wir setzen <strong className="font-semibold text-ink">Spark Ads als Hauptformat</strong> ein, weil sie native Authentizität mit Performance-Skalierung verbinden. Wir bauen ein Creator-Netzwerk auf, das nicht aus prominenten Influencern besteht, sondern aus passenden Mikro- und Nano-Creators. Wir tracken über TikTok Pixel plus Events API mit aktivem Match-Quality-Monitoring, nicht im Default-Setup.</p>
              <p>Konkret heißt das: Über unser Tool <Link to="/leistungen/picazzoo" className="underline">picazzoo</Link> produzieren wir TikTok-taugliche Asset-Variationen in Markentonalität, wo Creator-Content nicht ausreicht oder nicht skaliert. Über <Link to="/leistungen/gonzalads" className="underline">GonzalAds</Link> verwalten wir Bulk-Operationen schneller als der Native Ads Manager. Und wir steuern mit der Klarheit, dass TikTok andere Funnel-Mechanismen hat als Meta oder Google: Awareness-zu-Consideration-Sprünge sind kürzer, der Discovery-Effekt ist stärker, aber die Conversion-Stabilität ist niedriger. Das verlangt nach anderen KPIs, nicht nach kopierten Meta-Strategien.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Zielgruppen-Einordnung" headline={<>Wann Sie eine TikTok Ads Agentur <em>brauchen — und wann nicht.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {audience.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Methodik" headline={<>Wie unsere TikTok Ads Agentur <em>Mandate startet.</em></>}>
          <ProcessFlow steps={steps} />
          <RevealOnScroll>
            <div className="mt-10 max-w-2xl rounded-lg border border-rule bg-bg-subtle p-7">
              <p className="eyebrow mb-3">Schneller Start als Wettbewerbs-Vorteil</p>
              <p className="text-[15px] leading-relaxed text-ink-2">Während klassische Influencer-Agenturen oft sechs bis zwölf Wochen brauchen, bevor die ersten Spark Ads mit sauberer Performance-Anbindung laufen, sind wir innerhalb von zwei bis drei Wochen produktiv. Möglich ist das, weil wir Creator-Sourcing nicht als Talent-Management verkaufen, sondern als Performance-Hebel mit klaren wirtschaftlichen Zielen aufsetzen.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="05" kicker="Was wir liefern" headline={<>Was unsere TikTok Ads Agentur <em>konkret liefert.</em></>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section number="06" kicker="Tooling" headline={<>Tools und Stack unserer <em>TikTok-Ads-Steuerung.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Native Plattform</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>TikTok Ads Manager</li>
                  <li>TikTok Creative Center</li>
                  <li>TikTok Pixel + Events API</li>
                  <li>Creator Marketplace (selektiv)</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Tracking & Analytics</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Server-side Events API via GTM-Server</li>
                  <li>Google Analytics 4</li>
                  <li>Match-Quality-Score-Monitoring</li>
                  <li>Looker Studio Dashboards</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Calvarius-Tools</p>
                <ul className="space-y-3 text-sm text-ink-2">
                  <li><Link to="/leistungen/picazzoo" className="font-medium text-ink hover:text-cyan-deep">picazzoo →</Link><br/><span className="text-ink-3">KI-Asset-Produktion in TikTok-Format</span></li>
                  <li><Link to="/leistungen/gonzalads" className="font-medium text-ink hover:text-cyan-deep">GonzalAds →</Link><br/><span className="text-ink-3">Account-Operations auch für TikTok</span></li>
                </ul>
              </div>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="07" kicker="Was realistisch ist" headline={<>Realistische Ergebnisse einer <em>TikTok-Ads-Optimierung.</em></>}
          caption="TikTok ist im DACH-Markt ein dynamischer Kanal — Auktions-Bedingungen ändern sich mit der Marktreife.">
          <RevealOnScroll>
            <ul className="space-y-4">
              {realistic.map((r, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-rule bg-white p-6">
                  <span className="font-mono text-xs text-cyan-deep">{String(i+1).padStart(2,"0")}</span>
                  <p className="text-[15px] leading-relaxed text-ink-2">{r}</p>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        <Section number="08" kicker="Was Sie gewinnen" headline={<>Was Sie von unserer <em>TikTok Ads Agentur</em> bekommen.</>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {value.map(([h, b]) => (
                <div key={h} className="bg-white p-7">
                  <div className="font-display text-lg text-ink">{h}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <RelatedChannels locale="de" items={channelLinks} />

        <Section variant="tinted" number="09" kicker="Weiter vertiefen" headline={<>Verwandte Disziplinen.</>}
          caption="Diese Themen wirken im Zusammenspiel.">
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="group flex flex-col rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-lime hover:shadow-[0_12px_40px_-20px_oklch(0.85_0.18_125/0.5)]">
                  <h3 className="font-display text-xl font-semibold text-ink">{r.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-3">{r.sub}</p>
                  <span aria-hidden className="mt-5 font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-lime-deep">→</span>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/leistungen/performance-marketing" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">
                ← Alle Performance-Marketing-Disziplinen
              </Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="10" kicker="Konkret werden">
          <Callout>
            <strong>Im strategischen Zusammenhang:</strong> TikTok Ads ist die Performance-Tactic für bezahlte Reichweite auf TikTok. Wer die Plattform als Teil einer integrierten Social-Strategie betrachtet — Organic-Content plus Paid-Verstärkung als gemeinsames System — findet die methodische Einordnung unter <Link to="/leistungen/social-media-marketing" className="underline">Social Media Marketing →</Link>.
          </Callout>
          <CTABlock
            headline={<>TikTok Ads Audit in 30 Minuten — <em>kostenlos und unverbindlich.</em></>}
            body="In einem ersten Gespräch klären wir, ob TikTok Ads in Ihrem B2C-Setup wirtschaftlich Sinn macht — und wenn ja, mit welcher Priorität gegenüber Meta und Google Ads. Sie bekommen eine ehrliche Einordnung."
            primary={{ label: "Gespräch vereinbaren", to: "/kontakt" }}
            secondary={{ label: "Performance-Marketing-Übersicht", to: "/leistungen/performance-marketing" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
